import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Camera, Loader2, Gift, DollarSign, Trash2, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
function Settings() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [artistName, setArtistName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [socialLinks, setSocialLinks] = useState({
    instagram: "",
    twitter: "",
    spotify: "",
    soundcloud: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  // Password change state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [redeemingPromo, setRedeemingPromo] = useState(false);

  // Delete account state
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        setLoading(false);
        return;
      }
      setEmail(session.user.email || "");
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();
        if (error) throw error;
        setProfile(data);
        setUsername(data?.username || "");
        setArtistName(data?.artist_name || "");
        setBio(data?.bio || "");
        setAvatarUrl(data?.avatar_url || "");
        // Parse social_links - handle both object and string formats
        const links = data?.social_links;
        if (links && typeof links === 'object') {
          setSocialLinks({
            instagram: links.instagram || "",
            twitter: links.twitter || "",
            spotify: links.spotify || "",
            soundcloud: links.soundcloud || "",
          });
        }
      } catch (err) {
        toast.error(err?.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.id) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingAvatar(true);
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `avatar.${fileExt}`;
      const filePath = `${profile.id}/${fileName}`;

      // Delete existing avatar if present
      await supabase.storage
        .from('avatars')
        .remove([filePath]);

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const newAvatarUrl = urlData.publicUrl;
      
      // Update profile in database
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: newAvatarUrl })
        .eq("id", profile.id);

      if (updateError) throw updateError;

      // Add cache-buster to force image refresh
      setAvatarUrl(`${newAvatarUrl}?t=${Date.now()}`);

      toast.success("Avatar updated!");
    } catch (err) {
      console.error('Avatar upload error:', err);
      toast.error(err?.message || "Failed to upload avatar. Please try again.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile?.id) return;
    setSaving(true);
    try {
      const updates = { 
        username, 
        artist_name: artistName,
        bio,
      };
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", profile.id);
      if (error) throw error;
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSocialLinks = async (e) => {
    e.preventDefault();
    if (!profile?.id) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ social_links: socialLinks })
        .eq("id", profile.id);
      
      if (error) throw error;
      
      toast.success("Social links updated");
    } catch (err) {
      toast.error(err?.message || "Failed to save social links");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in both password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
      toast.success("Password updated! You can now sign in with your new password.");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err?.message || "Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSignOut = async () => {
    // Clear any legacy auth data
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('auth_token');
      window.localStorage.removeItem('auth_user');
    }
    await supabase.auth.signOut();
    // Force full page reload to clear all cached data
    window.location.href = "/Auth";
  };

  const handleDeleteAccount = async () => {
    if (!profile?.id) return;
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }

    setDeletingAccount(true);
    try {
      // Delete user's tracks
      await supabase.from("tracks").delete().eq("artist_id", profile.id);
      
      // Delete user's feedback
      await supabase.from("feedback").delete().eq("reviewer_id", profile.id);
      
      // Delete user's votes  
      await supabase.from("votes").delete().eq("voter_id", profile.id);
      
      // Delete user's notifications
      await supabase.from("notifications").delete().eq("user_id", profile.id);
      
      // Delete user's messages
      await supabase.from("messages").delete().or(`sender_id.eq.${profile.id},recipient_id.eq.${profile.id}`);
      
      // Delete user's profile
      await supabase.from("profiles").delete().eq("id", profile.id);
      
      // Delete avatar from storage
      await supabase.storage.from("avatars").remove([`${profile.id}/avatar.png`, `${profile.id}/avatar.jpg`, `${profile.id}/avatar.jpeg`, `${profile.id}/avatar.webp`]);

      // Sign out and clear local data
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('auth_token');
        window.localStorage.removeItem('auth_user');
      }
      localStorage.clear();
      await supabase.auth.signOut();
      
      toast.success("Account deleted successfully");
      window.location.href = "/";
    } catch (err) {
      console.error('Delete account error:', err);
      toast.error(err?.message || "Failed to delete account. Please contact support.");
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleSocialLinkChange = (platform, value) => {
    setSocialLinks(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  const handleRedeemPromo = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    setRedeemingPromo(true);
    try {
      const { data, error } = await supabase.rpc('redeem_promo_code', {
        p_code: promoCode.trim()
      });
      
      if (error) throw error;
      
      if (!data?.success) {
        throw new Error(data?.error || "Failed to redeem promo code");
      }
      
      toast.success(`Promo code redeemed! +${data.credits_awarded} credits added.`);
      setPromoCode("");
    } catch (err) {
      toast.error(err?.message || "Failed to redeem promo code");
    } finally {
      setRedeemingPromo(false);
    }
  };
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#c0c0c0]">
        Loading settings...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-[#e0e0e0] px-4 py-10">
      <div className="max-w-3xl mx-auto space-y-6">
        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm text-[#a0a0a0]">Email</label>
              <Input value={email} disabled className="bg-[#0f0f0f] border-[#1f1f1f]" />
            </div>
            <Button variant="destructive" onClick={handleSignOut}>
              Sign Out
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle>Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-6" onSubmit={handleSave}>
              {/* Avatar Section */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="w-20 h-20 border-2 border-[#2a2a2a]">
                    <AvatarImage src={avatarUrl} alt="Profile" />
                    <AvatarFallback className="bg-[#1a1a1a] text-[#808080] text-2xl">
                      {artistName?.[0] || username?.[0] || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#2a2a2a] border border-[#3a3a3a] flex items-center justify-center hover:bg-[#3a3a3a] transition-colors"
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#a0a0a0]" />
                    ) : (
                      <Camera className="w-4 h-4 text-[#a0a0a0]" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                </div>
                <div className="text-sm text-[#707070]">
                  Click the camera icon to upload a profile picture
                </div>
              </div>

              <div>
                <label className="text-sm text-[#a0a0a0]">Username</label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>
              
              <div>
                <label className="text-sm text-[#a0a0a0]">Artist Name</label>
                <Input
                  value={artistName}
                  onChange={(e) => setArtistName(e.target.value)}
                  placeholder="Your artist/display name"
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>

              <div>
                <label className="text-sm text-[#a0a0a0]">Bio</label>
                <Textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell us about yourself and your music... (min 20 characters for profile completion)"
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0] min-h-[100px]"
                />
                <p className="text-xs text-[#606060] mt-1">
                  {bio.length} characters {bio.length < 20 && "(need 20+ for completion)"}
                </p>
              </div>

              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle>Social Links</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSaveSocialLinks}>
              <div>
                <label className="text-sm text-[#a0a0a0]">Instagram</label>
                <Input
                  value={socialLinks.instagram}
                  onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
                  placeholder="https://instagram.com/yourusername"
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>
              <div>
                <label className="text-sm text-[#a0a0a0]">Twitter / X</label>
                <Input
                  value={socialLinks.twitter}
                  onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
                  placeholder="https://twitter.com/yourusername"
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>
              <div>
                <label className="text-sm text-[#a0a0a0]">Spotify</label>
                <Input
                  value={socialLinks.spotify}
                  onChange={(e) => handleSocialLinkChange('spotify', e.target.value)}
                  placeholder="https://open.spotify.com/artist/..."
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>
              <div>
                <label className="text-sm text-[#a0a0a0]">SoundCloud</label>
                <Input
                  value={socialLinks.soundcloud}
                  onChange={(e) => handleSocialLinkChange('soundcloud', e.target.value)}
                  placeholder="https://soundcloud.com/yourusername"
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>
              <p className="text-xs text-[#606060]">
                Add at least one social link for profile completion
              </p>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Social Links"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleChangePassword}>
              <div>
                <label className="text-sm text-[#a0a0a0]">New Password</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>
              <div>
                <label className="text-sm text-[#a0a0a0]">Confirm New Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>
              <Button type="submit" disabled={changingPassword}>
                {changingPassword ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#90b090]" />
              Promo Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleRedeemPromo}>
              <div>
                <label className="text-sm text-[#a0a0a0]">Enter Promo Code</label>
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="e.g. WELCOME5"
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0] uppercase"
                />
              </div>
              <Button type="submit" disabled={redeemingPromo || !promoCode.trim()}>
                {redeemingPromo ? "Redeeming..." : "Redeem Code"}
              </Button>
              <p className="text-xs text-[#606060]">
                Have a promo code? Enter it above to get free credits!
              </p>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Cash Out Earnings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[#a0a0a0]">
              Earned money from writing reviews? Cash out your earnings via PayPal.
            </p>
            <Button 
              onClick={() => navigate('/cashout')}
              variant="outline"
              className="w-full"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Go to Cashout
            </Button>
          </CardContent>
        </Card>

        {/* Delete Account - Danger Zone */}
        <Card className="bg-[#121212] border border-red-900/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[#a0a0a0]">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#121212] border border-[#2a2a2a]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-red-500 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Delete Account Permanently?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-[#a0a0a0] space-y-3">
                    <p>This will permanently delete:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Your profile and all personal data</li>
                      <li>All tracks you've uploaded</li>
                      <li>All feedback and reviews you've written</li>
                      <li>Your credits and earnings history</li>
                      <li>All messages and notifications</li>
                    </ul>
                    <p className="font-semibold text-red-400 mt-4">
                      This action is irreversible!
                    </p>
                    <div className="mt-4">
                      <label className="text-sm text-[#808080] block mb-2">
                        Type <span className="font-mono text-red-400">DELETE</span> to confirm:
                      </label>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                        placeholder="Type DELETE"
                        className="bg-[#0a0a0a] border-red-900/50 text-[#e0e0e0]"
                      />
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel 
                    className="bg-[#1a1a1a] border-[#2a2a2a] text-[#e0e0e0] hover:bg-[#2a2a2a]"
                    onClick={() => setDeleteConfirmText("")}
                  >
                    Cancel
                  </AlertDialogCancel>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== "DELETE" || deletingAccount}
                  >
                    {deletingAccount ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Forever
                      </>
                    )}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Settings;
